import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuditService, AuditDto, AuditStatus } from '../../api';
import { Observable } from 'rxjs';
import { environment } from 'src/assets/env/environment';
import { AuditListItemDto } from '../../features/audits/state/audit-list-item.model';

/**
 * Audit Service
 *
 * Pure API wrapper for audit-related operations.
 * All HTTP errors are automatically handled by HttpErrorInterceptor.
 *
 * State Management:
 * - Use NgRx Store for list management (see AuditsComponent + Effects)
 * - This service provides ONLY API access methods
 *
 * Features:
 * - CRUD operations for audits
 * - Type-safe methods
 * - Automatic error handling via interceptor
 * - API encapsulation (endpoint names stay internal)
 */
@Injectable({
  providedIn: 'root'
})
export class MAuditService {
  constructor(
    private readonly auditService: AuditService,
    private readonly http: HttpClient,
  ) {}

  /**
   * Gets the documentation list of the current user and their organisation.
   *
   * DOCU-1220: slim rows without form snapshots and entries (the old
   * `GET /audit` was ~19 MB for 31 FUCHS audits). Hand-written call, auth via
   * token interceptor.
   *
   * NOTE: For list management, use NgRx Store (dispatch loadAudits action).
   */
  getUserAudits(): Observable<AuditListItemDto[]> {
    return this.http.get<AuditListItemDto[]>(`${environment.API_BASE_URL}/api/v1/audit/list`);
  }

  /**
   * Gets a single audit by ID.
   *
   * @param id - The audit ID
   * @returns Observable of the audit
   */
  getAuditById(id: string): Observable<AuditDto> {
    return this.auditService.getAuditEndpoint(id);
  }

  /**
   * Creates a new audit.
   *
   * @param audit - The audit to create
   * @param assigneeUserId - DOCU-673: optional owner override. When set, the
   *   audit is owned by this org user (created_by stays the calling user) and
   *   reaches that user's device via the regular sync download.
   * @returns Observable of the created audit
   */
  createAudit(audit: AuditDto, assigneeUserId?: string | null): Observable<AuditDto> {
    return this.auditService.postAuditEndpoint({ audit, assigneeUserId });
  }

  /**
   * DOCU-675: reassigns an existing audit to another user of the organisation.
   * Hand-written call (endpoint not yet in the generated client, same pattern
   * as ExportTemplateService/DOCU-722); auth via token interceptor.
   *
   * @param auditId - The audit ID
   * @param newOwnerUserId - The org user who becomes the new owner
   */
  reassignOwner(auditId: string, newOwnerUserId: string): Observable<void> {
    return this.http.patch<void>(
      `${environment.API_BASE_URL}/api/v1/audit/${auditId}/owner`,
      { newOwnerUserId },
    );
  }

  /**
   * Deletes an audit by ID.
   *
   * @param auditId - The audit ID to delete
   * @returns Observable of the deleted audit
   */
  deleteAudit(auditId: string): Observable<AuditDto> {
    return this.auditService.deleteAuditEndpoint(auditId);
  }

  /**
   * Updates the status of an audit.
   *
   * @param auditId - The audit ID
   * @param status - The new status for the audit
   * @returns Observable of the updated audit status
   */
  updateAuditStatus(auditId: string, status: AuditStatus): Observable<any> {
    return this.auditService.updateAuditStatusEndpoint(auditId, { status });
  }
}
